import { useStore } from '../store/useStore'
import { PLANS, stripeService } from '../services/stripe'
import { Button } from '../components/UI'
import { useState } from 'react'
import toast from 'react-hot-toast'

const C = { mint: '#00C896', text: '#E8F5EE', muted: '#6B8F7A', border: '#1E2E24', surface: '#111916', surfaceAlt: '#161F1A', purple: '#A78BFA', bg: '#0A0F0D' }

export default function Pricing() {
  const { user, plan: currentPlan } = useStore()
  const [billing, setBilling] = useState('monthly') // 'monthly' | 'yearly'

  const handleUpgrade = async (planKey) => {
    if (!user) { window.location.href = '/login?redirect=pricing'; return }
    const plan = PLANS[planKey]
    if (!plan.priceId) { toast.error('Price ID not configured'); return }
    try {
      await stripeService.createCheckoutSession(user.id, plan.priceId, planKey)
    } catch {
      toast.error('Something went wrong. Please try again.')
    }
  }

  const yearlyDiscount = (price) => (price * 12 * 0.8).toFixed(0) // 20% off

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '80px 24px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 60 }}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 40, textDecoration: 'none' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: C.mint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🌿</div>
          <span style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18 }}>Peppermint<span style={{ color: C.mint }}>Fi</span></span>
        </a>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 'clamp(36px,5vw,56px)', fontWeight: 900, color: C.text, lineHeight: 1, marginBottom: 16 }}>
          Simple, honest pricing
        </h1>
        <p style={{ color: C.muted, fontSize: 18, marginBottom: 32 }}>Start free. Upgrade when you're ready.</p>

        {/* Billing toggle */}
        <div style={{ display: 'inline-flex', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 99, padding: 4 }}>
          {['monthly', 'yearly'].map(b => (
            <button key={b} onClick={() => setBilling(b)} style={{
              padding: '8px 20px', borderRadius: 99, border: 'none', cursor: 'pointer',
              background: billing === b ? C.mint : 'transparent',
              color: billing === b ? '#000' : C.muted,
              fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 600,
              transition: 'all 0.2s',
            }}>
              {b === 'yearly' ? 'Yearly (Save 20%)' : 'Monthly'}
            </button>
          ))}
        </div>
      </div>

      {/* Plans */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20, maxWidth: 960, margin: '0 auto 60px' }}>
        {Object.entries(PLANS).map(([key, plan]) => {
          const isCurrent = currentPlan === key
          const isPro = key === 'pro'
          const isPremium = key === 'premium'
          const displayPrice = billing === 'yearly' && plan.price > 0
            ? (plan.price * 0.8).toFixed(2)
            : plan.price.toFixed(2)

          return (
            <div key={key} style={{
              background: isPro ? `linear-gradient(135deg, ${C.surface}, #0D1F16)` : C.surface,
              border: `1px solid ${isPro ? 'rgba(0,200,150,0.35)' : isPremium ? 'rgba(167,139,250,0.25)' : C.border}`,
              borderRadius: 20, padding: 32, position: 'relative', overflow: 'hidden',
              boxShadow: isPro ? '0 0 40px rgba(0,200,150,0.08)' : 'none',
            }}>
              {isPro && (
                <div style={{
                  position: 'absolute', top: 16, right: 16,
                  background: C.mint, color: '#000', fontSize: 11, fontWeight: 700,
                  padding: '4px 12px', borderRadius: 99, letterSpacing: '0.08em',
                }}>MOST POPULAR</div>
              )}

              <div style={{ marginBottom: 24 }}>
                <div style={{ color: isPremium ? C.purple : isPro ? C.mint : C.muted, fontSize: 13, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                  {plan.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 44, fontWeight: 900, color: C.text }}>
                    {plan.price === 0 ? 'Free' : `$${displayPrice}`}
                  </span>
                  {plan.price > 0 && <span style={{ color: C.muted, fontSize: 14 }}>/mo</span>}
                </div>
                {billing === 'yearly' && plan.price > 0 && (
                  <div style={{ color: C.mint, fontSize: 12, marginTop: 4 }}>
                    ${yearlyDiscount(plan.price)}/year — save ${(plan.price * 12 * 0.2).toFixed(0)}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 28 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: C.mint, flexShrink: 0, marginTop: 1 }}>✓</span>
                    <span style={{ color: C.muted, fontSize: 14, lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>

              {isCurrent ? (
                <div style={{
                  width: '100%', padding: '13px', borderRadius: 12, textAlign: 'center',
                  background: 'rgba(0,200,150,0.1)', border: `1px solid rgba(0,200,150,0.25)`,
                  color: C.mint, fontSize: 14, fontWeight: 600,
                }}>Current Plan ✓</div>
              ) : (
                <button onClick={() => key !== 'free' && handleUpgrade(key)} style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                  background: isPro ? C.mint : isPremium ? C.purple : `1px solid ${C.border}`,
                  color: (isPro || isPremium) ? '#000' : C.text,
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  transition: 'opacity 0.2s',
                }}>
                  {key === 'free' ? 'Get Started Free' : `Upgrade to ${plan.name}`}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* FAQ */}
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, color: C.text, marginBottom: 8 }}>Questions?</h2>
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 8 }}>Cancel anytime, no questions asked. Bank-grade 256-bit encryption. Your data is never sold.</p>
        <a href="mailto:hello@peppermintfi.com" style={{ color: C.mint, fontSize: 14, textDecoration: 'none' }}>hello@peppermintfi.com</a>
      </div>
    </div>
  )
}
